import React, { createContext, useContext, useMemo } from 'react';
import { EventPrivateDetailsResponseType, EventPublicDetailsResponseType } from '../../api';
import { useEventPrivateInfo } from '../../../hooks/api/protected/use-event-private-details';

import {
  composersList,
  AgendaProps, LandingPageProps, SessionsProps, ShowcaseProps, SpeakersProps, SupportProps, WelcomeProps, ReplaysProps,
} from './settings-composers';
import { IAuthContext, useAuth } from '../auth-context';
import { useEventPublicInfo } from '../../../hooks/api/public/use-event-public-details';


export type EventPrivateSettingsType = LandingPageProps & ShowcaseProps & WelcomeProps & SupportProps & AgendaProps & SessionsProps & SpeakersProps & ReplaysProps;

export type EventSettingsContextType = EventPrivateDetailsResponseType & EventPrivateSettingsType & {
  pageFlagsAuthoritative?: boolean;
};

export type EventPublicContextType = EventPublicDetailsResponseType;

const EventSettingsContext = createContext<EventSettingsContextType | void>(undefined);
export const useEventSettings = (): EventSettingsContextType | void => useContext(EventSettingsContext);

export const arePageFlagsPending = (
  settings?: { pageFlagsAuthoritative?: boolean } | null | void
): boolean => settings?.pageFlagsAuthoritative === false;

const composeSettingsFromPages = (
  data: EventPrivateDetailsResponseType | EventPublicDetailsResponseType
): EventSettingsContextType => {
  const customPages = data.customPages || [];
  const pagesProps = composersList
    .reduce((props, composer) => ({ ...props, ...composer(customPages) }), {} as EventPrivateSettingsType)
  ;

  return { ...data, ...pagesProps } as EventSettingsContextType;
};

const useEventSettingsProvider = (): EventSettingsContextType | EventPublicContextType | void => {
  const auth = useAuth() as IAuthContext;
  const isAuthenticated = auth?.isAuthenticated;

  const { data: privateData, error: privateError, isValidating: isPrivateValidating } = useEventPrivateInfo();
  const { data: publicData, error: publicError, isValidating: isPublicValidating } = useEventPublicInfo();
  const privateRequestFailed = Boolean(privateError) && !privateData && !isPrivateValidating;
  const publicRequestFailed = Boolean(publicError) && !publicData && !isPublicValidating;

  return useMemo(() => {
    if (isAuthenticated !== false && privateData) {
      return {
        ...composeSettingsFromPages(privateData),
        pageFlagsAuthoritative: isAuthenticated === true
      };
    }

    if (isAuthenticated === false) {
      if (publicData) {
        return { ...publicData, pageFlagsAuthoritative: true };
      }
      // Public details failed and will not retry. Allow the catch-all to leave a blank Switch.
      if (publicRequestFailed) {
        return { pageFlagsAuthoritative: true } as EventSettingsContextType;
      }
      return undefined;
    }

    if (privateError && publicData) {
      return {
        ...composeSettingsFromPages(publicData),
        pageFlagsAuthoritative: true
      };
    }

    // Both payloads failed and will not retry. The catch-all can redirect an unmatched essential prefix.
    if (isAuthenticated === true && privateRequestFailed && publicRequestFailed) {
      return { pageFlagsAuthoritative: true } as EventSettingsContextType;
    }

    return publicData
      ? { ...composeSettingsFromPages(publicData), pageFlagsAuthoritative: false }
      : undefined;
  }, [
    isAuthenticated,
    privateData,
    privateError,
    privateRequestFailed,
    publicData,
    publicRequestFailed
  ]);
};

type Props = { children: React.ReactNode | React.ReactNodeArray }
export const EventSettingsProvider: React.FunctionComponent<Props> = ({ children }: Props): JSX.Element => {
  const settings = useEventSettingsProvider() as EventSettingsContextType;

  return (
    <EventSettingsContext.Provider value={settings}>
      {children}
    </EventSettingsContext.Provider>
  );
};

export default EventSettingsProvider;
